import type { ModuleManifest, QuestionnaireDeclaration, QuestionnaireTemplate } from '@nexus/core';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminError } from '../admin/shared';
import { api } from '../api/client';
import type { SessionInfo } from '../api/types';
import { useOfflineRecords } from '../offline/offline';
import { canIn } from '../session/session';
import { useOnline } from '../sync/sync-status';

/** Kernel source that brings the templates onto the device (docs/architecture/offline.md). */
const OFFLINE_SOURCE = 'core.questionnaire-templates';

/** Base path of a questionnaire's API: `/templates`, `/order`, `/defaults` below it. */
export function questionnairePath(key: string): string {
  return `/questionnaires/${encodeURIComponent(key)}`;
}

/** All templates of a questionnaire in their order, inactive ones too. */
export function questionnaireTemplatesQuery(key: string) {
  return queryOptions({
    queryKey: ['questionnaires', key, 'templates'] as const,
    queryFn: ({ signal }) =>
      api
        .get<{ items: QuestionnaireTemplate[] }>(`${questionnairePath(key)}/templates`, { signal })
        .then((answer) => answer.items),
  });
}

/** A questionnaire with the module that declares it. */
export interface OwnedQuestionnaire extends QuestionnaireDeclaration {
  owner: string;
}

/** The module or one of its submodules declares a questionnaire: its settings have an editor. */
export function hasQuestionnaires(module: ModuleManifest, all: readonly ModuleManifest[]): boolean {
  return [module, ...all.filter((m) => m.parent === module.id)].some(
    (m) => (m.questionnaires ?? []).length > 0,
  );
}

/**
 * Questionnaires of a module and its active submodules whose templates the person maintains
 * (`<module>.settings.manage`).
 */
export function questionnairesOfModule(
  module: ModuleManifest,
  all: readonly ModuleManifest[],
  session: SessionInfo,
): OwnedQuestionnaire[] {
  const active = new Set(session.modules);
  return [module, ...all.filter((m) => m.parent === module.id)]
    .filter((m) => active.has(m.id) && canIn(session, `${m.id}.settings.manage`))
    .flatMap((m) => (m.questionnaires ?? []).map((q) => ({ ...q, owner: m.id })));
}

/** Toast for a failed change of templates, with the reasons the API names. */
export function useTemplateError(): (error: unknown) => void {
  const { t } = useTranslation();
  const reportError = useAdminError();
  return useCallback(
    (error) =>
      reportError(error, {
        'template-name-taken': t('questionnaires.errors.template-name-taken'),
        'questionnaire-templates-limit': t('questionnaires.errors.questionnaire-templates-limit'),
        'order-incomplete': t('questionnaires.errors.order-incomplete'),
      }),
    [reportError, t],
  );
}

export interface QuestionnaireTemplates {
  /** Active templates in their order. */
  templates: QuestionnaireTemplate[];
  /** The default template while it is active, otherwise the first active one. */
  defaultTemplate: QuestionnaireTemplate | null;
  loading: boolean;
  /** Neither the server nor the device has the templates, e.g. offline before the first sync. */
  unavailable: boolean;
}

function byOrder(a: QuestionnaireTemplate, b: QuestionnaireTemplate): number {
  return a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt);
}

/**
 * Templates for filling in a questionnaire: online from the API, offline from the device (kernel
 * source `core.questionnaire-templates`), also when the request fails.
 */
export function useQuestionnaireTemplates(key: string): QuestionnaireTemplates {
  const online = useOnline();
  const query = useQuery({ ...questionnaireTemplatesQuery(key), enabled: online });
  const device = useOfflineRecords(OFFLINE_SOURCE);
  const stored = useMemo(
    () =>
      device.records
        .map((record) => record.data as unknown as QuestionnaireTemplate)
        .filter((template) => template.questionnaire === key)
        .sort(byOrder),
    [device.records, key],
  );
  const all = query.data ?? (device.available ? stored : undefined);
  const templates = useMemo(() => (all ?? []).filter((template) => template.active), [all]);
  return {
    templates,
    defaultTemplate: templates.find((template) => template.isDefault) ?? templates[0] ?? null,
    loading: !all && online && query.isPending,
    unavailable: !all && (!online || query.isError),
  };
}
